import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { timestampByUTC } from '../helpers/dateTime';
import { pushThirdPartySummaries } from '@/api/cms/thirdPartySummaries';
import { PlayerJackpotWin } from '@/api/common';
import { Modal } from '@/components/Modal';
import { JackpotWinnerModal } from '@/components/modals/JackpotWinnerModal';
import { findAvailableModal } from '@/helpers/thirdPartySummaries';
import { useCommonStore } from '@/store';

const formatModalId = (createdAt: string) =>
  `JACKPOT_WIN_${timestampByUTC(createdAt)}`;

export const useJackpotWinnerModal = () => {
  const { user, playerJackpotWins, thirdPartySummaries } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      playerJackpotWins: state.playerJackpotWins,
      thirdPartySummaries: state.thirdPartySummaries,
    }))
  );

  const [isOpenJackpot, setOpenJackpot] = useState<PlayerJackpotWin>();

  const availableModal = useMemo(() => {
    if (!user?.id || !playerJackpotWins?.length) return;

    const ids = playerJackpotWins.map(({ createdAt }) =>
      formatModalId(createdAt)
    );
    const modalId = thirdPartySummaries
      ? findAvailableModal(ids)(thirdPartySummaries)
      : undefined;

    return {
      modalId: modalId || undefined,
      modalData: modalId ? playerJackpotWins[ids.indexOf(modalId)] : undefined,
    };
  }, [playerJackpotWins, thirdPartySummaries, user]);

  useEffect(() => {
    if (!user?.id || !availableModal?.modalData || !availableModal?.modalId)
      return;

    setOpenJackpot(availableModal.modalData);
    pushThirdPartySummaries({
      userId: user?.id,
      modalId: availableModal.modalId,
    });
  }, [availableModal, user?.id]);

  return isOpenJackpot ? (
    <Modal>
      <JackpotWinnerModal
        onClose={() => setOpenJackpot(undefined)}
        jackpotWin={isOpenJackpot}
      />
    </Modal>
  ) : null;
};
